// package imports
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { renderHTML } from '@agility/nextjs';
import { styled } from '@mui/material/styles';
import { KitButton } from 'components/common/cms-kit';
import Typography from '@mui/material/Typography';
import { Theme as MuiTheme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

// local imports
import { FreeformHeader } from 'components/common/Freeform/FreeformHeader';
import { headingColor } from 'services/styleUtils/headingStyleUtils';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';
import {
  AgilityContentProperties,
  AgilityImage,
  ContentStyle,
  HeadingStyle,
  TailwindFontColorItem,
} from 'types/AgilityTypes';
import { logClientException } from 'appinsights/clientAppInsights';
import { getContentStyle } from 'services/styleUtils/contentStyleUtils';
import CustomSubTitle from 'components/training/CustomSubTitle';
import AIDisclaimer from 'components/common/AIDisclaimer';
import { cn } from 'services/styleUtils/cn';
import useMediaQuery from 'hooks/useMediaQuery';
import VideoDialogWithX from './pageComponents/VideoDialogWithX';
import GlassCard from 'components/common/subcomponents/GlassCard';

type CTALinkType =
  | 'anchor'
  | 'page'
  | 'tab'
  | 'video'
  | 'Video'
  | 'elementClick';

type CTAUrl = { href: string; text: string; target: string };

type TopHeadingModule = {
  contentID: number;
  properties: AgilityContentProperties;
  fields: {
    fullScreen?: string;
    backgroundImage: AgilityImage;
    noGradient?: undefined | 'true' | 'false';
    displayCTAButton?: undefined | 'yes' | 'no';
    ctaButtonText: string;
    ctaLinkType: CTALinkType;
    ctaLink: string;
    ctaURL: CTAUrl;
    ctaVideoEmbedCode?: string;
    ctaDisclaimerText?: string;
    ctaDisclaimerTextFontColor?: TailwindFontColorItem;
    ctaDisclaimerContent?: string;
    buttonColor: string;
    buttonBorderColor: string;
    buttonTextColor: string;

    displayCTA2Button?: undefined | 'yes' | 'no';
    cta2ButtonText: string;
    cta2LinkType: CTALinkType;
    cta2Link: string;
    cta2URL: CTAUrl;
    cta2VideoEmbedCode?: string;
    cta2DisclaimerText?: string;
    cta2DisclaimerTextFontColor?: TailwindFontColorItem;
    cta2DisclaimerContent?: string;
    button2Color: string;
    button2BorderColor: string;
    button2TextColor: string;

    headingText: string;
    headingLevel: string;
    headingStyle: HeadingStyle;
    headingStyle_ValueField: string;
    descriptionText: string;
    contentStyle: ContentStyle;
    subtitle: string;
    subtitleStyle: ContentStyle;

    displayContentInCard?: undefined | 'true' | 'false';
  };
};

const Description = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: '1.5rem',
  lineHeight: '2rem',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  width: '642px',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));
const MobileDescription = styled(Typography)<{ hasSubtitle: boolean }>(
  ({ theme, hasSubtitle }: { theme: any; hasSubtitle: boolean }) => ({
    color: theme.palette.common.white,
    fontSize: '1.125rem',
    lineHeight: '1.438rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    width: '100%',
    padding: hasSubtitle ? '.75rem 0' : '5% 0',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  }),
);
const ButtonContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    margin: '30px 0 80px 0',
    alignSelf: 'flex-start',
    bottom: '-180px',
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
}));

const CTAColumnsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '0.5rem',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '1rem',
  },
}));

const CTAColumn = styled('div')({
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: 'auto',
  maxWidth: '100%',
});

const CTAButtonWrapper = styled('div')({
  display: 'inline-flex',
  width: 'fit-content',
  maxWidth: '100%',
});

const DisclaimerContainer = styled('div')({
  marginTop: '0.75rem',
  width: '100%',
  maxWidth: '100%',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
});

const VideoContainer = styled('div')({
  flexGrow: 0,
  flexShrink: 1,
  '& .video': {
    aspectRatio: '16 / 9',
    width: '800px',
  },
});

const EmbedContainer = styled('div')({
  '@media screen and (min-width: 992px)': {
    width: '800px',
  },
  '@media screen and (max-width: 991px)': {
    width: '100%',
  },
});

const VideoPlaceholderContainer = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: '300px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.grey[900],
}));

const Container = styled('div')({
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
  '@media screen and (min-width: 900px)': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundSize: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left 10% top 10%',
    position: 'relative',
    padding: '10px 20px',
  },
});
const MobileContainer = styled('div')({
  '@media screen and (max-width: 899px)': {
    minHeight: '320px',
    height: 'auto',
    //height: '320px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundSize: '200%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 40% top 10%',
    position: 'relative',
    padding: '60px 10px 10px 10px',
    margin: '0',
  },
  '@media screen and (min-width: 900px)': {
    display: 'none',
  },
});
const TextContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',

  '@media screen and (max-width: 899px)': {
    margin: '0 5%',
    width: '90%',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
  '@media screen and (min-width: 900px)': {
    height: 'inherit',
    margin: '50px 15% 0 15%',
    width: '90%',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
});

const ContentContainer = ({
  children,
  displayContentInCard,
}: {
  children: React.ReactNode;
  displayContentInCard: boolean;
}) =>
  displayContentInCard ? <GlassCard>{children}</GlassCard> : <>{children}</>;

const TopBannerWithHeading = ({ module }: { module: TopHeadingModule }) => {
  const { fields } = module;

  const router = useRouter();
  const cta1ButtonRef = useRef<HTMLDivElement | null>(null);
  const cta2ButtonRef = useRef<HTMLDivElement | null>(null);
  const [cta1ColumnWidth, setCta1ColumnWidth] = useState<number | undefined>();
  const [cta2ColumnWidth, setCta2ColumnWidth] = useState<number | undefined>();
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [selectedVideoEmbedCode, setSelectedVideoEmbedCode] = useState('');
  const [shouldLoadVideoEmbed, setShouldLoadVideoEmbed] = useState(false);
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
  );

  const theme = useTheme() as MuiTheme;
  const descriptionStyleFields = fields?.contentStyle?.fields || {
    textSize: '2xl',
    textColor: 'white',
  };
  const descriptionStyle = getContentStyle(theme, descriptionStyleFields);
  const [subTitleStyle, setSubTitleStyle] = useState<
    ReturnType<typeof getContentStyle> & {
      fontSize?: string;
      lineHeight?: string;
      marginTop?: string;
      marginBottom?: string;
    }
  >(getContentStyle(theme, fields?.subtitleStyle?.fields || {}));
  const displayContentInCard = fields.displayContentInCard === 'true';

  const hasSubtitle = fields.subtitle && fields.subtitle.trim().length > 0;

  useEffect(() => {
    const checkIsMobile = () => {
      const isMobile = window.innerWidth < 768;
      const baseSubTitleStyle = getContentStyle(
        theme,
        fields?.subtitleStyle?.fields || {},
      );

      setSubTitleStyle({
        ...baseSubTitleStyle,
        fontSize: isMobile ? '19px' : baseSubTitleStyle.fontSize || '58px',
        lineHeight: isMobile ? '20px' : baseSubTitleStyle.lineHeight || '58px',
        marginTop: isMobile
          ? '.5rem'
          : baseSubTitleStyle?.marginTop || '.25rem',
      });
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, [fields?.subtitleStyle?.fields, theme]);

  useEffect(() => {
    const observeButtonWidth = (
      element: HTMLDivElement | null,
      setWidth: (width?: number) => void,
    ) => {
      if (!element || typeof ResizeObserver === 'undefined') {
        setWidth(undefined);
        return () => {};
      }

      const updateWidth = () => {
        const width = Math.ceil(element.getBoundingClientRect().width);
        setWidth(width || undefined);
      };

      updateWidth();
      const observer = new ResizeObserver(updateWidth);
      observer.observe(element);

      return () => observer.disconnect();
    };

    const cleanup1 = observeButtonWidth(
      cta1ButtonRef.current,
      setCta1ColumnWidth,
    );
    const cleanup2 = observeButtonWidth(
      cta2ButtonRef.current,
      setCta2ColumnWidth,
    );

    return () => {
      cleanup1();
      cleanup2();
    };
  }, [fields?.ctaButtonText, fields?.cta2ButtonText]);

  const handleVideoDialogVisibilityChange = (isOpen: boolean) => {
    setShowVideoDialog(isOpen);

    if (!isOpen) {
      setShouldLoadVideoEmbed(false);
      setSelectedVideoEmbedCode('');
    }
  };

  const handleCTAClick = ({
    ctaLinkType,
    ctaLink,
    ctaURL,
    ctaVideoEmbedCode,
  }: {
    ctaLinkType: CTALinkType;
    ctaLink: string;
    ctaURL: CTAUrl;
    ctaVideoEmbedCode?: string;
  }) => {
    const openCTAUrl = () => {
      if (!ctaURL?.href) {
        return;
      }

      if (ctaURL.target === '_blank') {
        window.open(ctaURL.href, '_blank');
      } else {
        router.push(ctaURL.href);
      }
    };

    if (ctaLinkType === 'elementClick' && ctaLink) {
      const element = document.getElementById(ctaLink);
      if (element) {
        element.click();
      } else {
        console.warn(`Element with ID ${ctaLink} not found.`);
        logClientException(new Error(`Element with ID ${ctaLink} not found.`));
      }
    } else if (ctaLinkType === 'anchor') {
      const element = document.getElementById(ctaLink);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' }); // Use 'auto' or omit for instant scroll
      }
    } else if (ctaLinkType?.toLowerCase() === 'video') {
      if (ctaVideoEmbedCode?.trim()) {
        setSelectedVideoEmbedCode(ctaVideoEmbedCode);
        setShouldLoadVideoEmbed(!prefersReducedMotion);
        setShowVideoDialog(true);
      } else {
        openCTAUrl();
      }
    } else if (
      ctaLinkType === 'tab' ||
      (ctaLinkType === 'page' && ctaURL?.target === '_blank')
    ) {
      window.open(ctaURL?.href, '_blank');
    } else {
      openCTAUrl();
    }
  };

  return (
    <React.Fragment>
      <Container
        style={{
          backgroundImage:
            fields.noGradient === 'true'
              ? `url(${fields.backgroundImage.url})`
              : `linear-gradient(to right, rgba(7, 30, 49, 1), rgba(5, 50, 77, 0.76), rgba(0, 85, 128, 0.32)), url(${fields.backgroundImage.url})`,
          backgroundColor: theme.palette.primary.dark,
          ...(fields.fullScreen && fields?.fullScreen === 'true'
            ? {
                maxWidth: '100%',
                width: '100%',
                margin: 0,
              }
            : {
                maxWidth: '1400px',
                margin: '25px auto',
              }),
        }}
      >
        <VideoContainer>
          <TextContainer
            style={displayContentInCard ? { marginTop: 0 } : undefined}
          >
            <ContentContainer displayContentInCard={displayContentInCard}>
              <FreeformHeader
                level={fields?.headingLevel || 'h1'}
                text={fields.headingText}
                color={headingColor(theme, fields?.headingStyle?.fields?.color)}
                style={{
                  margin: displayContentInCard
                    ? `0 0 ${fields.subtitle && fields.subtitle.trim().length > 0 ? '.25rem' : '1.5rem'} 0`
                    : `4rem 30% ${fields.subtitle && fields.subtitle.trim().length > 0 ? '.25rem' : '1.5rem'} 0`,
                  textAlign: displayContentInCard ? 'center' : undefined,
                }}
              />
              {fields.subtitle && fields.subtitle.trim().length > 0 && (
                <CustomSubTitle
                  subtitle={fields.subtitle}
                  subTitleStyle={{
                    textAlign: subTitleStyle?.alignment || '',
                    color: subTitleStyle?.textColor || '#FFF',
                    fontFamily: 'Source Sans Pro',
                    fontStyle: 'normal',
                    marginTop: subTitleStyle?.marginTop || 0,
                    ...(subTitleStyle || {}),
                  }}
                />
              )}
              <Description
                style={{
                  color: descriptionStyle?.textColor
                    ? descriptionStyle.textColor
                    : theme.palette.common.white,
                  fontSize: descriptionStyle?.fontSize
                    ? descriptionStyle.fontSize
                    : '1.5rem',
                  lineHeight: descriptionStyle?.lineHeight
                    ? descriptionStyle.lineHeight
                    : '2rem',
                  textAlign: displayContentInCard
                    ? 'center'
                    : descriptionStyle?.alignment,
                }}
              >
                {fields.descriptionText}
              </Description>
              <ButtonContainer
                style={
                  displayContentInCard
                    ? { alignSelf: 'center', marginBottom: 0 }
                    : undefined
                }
              >
                <CTAColumnsContainer
                  style={
                    displayContentInCard
                      ? { alignItems: 'flex-start' }
                      : undefined
                  }
                >
                  {(fields.displayCTAButton === 'yes' ||
                    (fields.ctaDisclaimerText &&
                      fields.ctaDisclaimerContent)) && (
                    <CTAColumn
                      style={
                        displayContentInCard
                          ? {
                              alignItems: 'center',
                              ...(fields.displayCTAButton === 'yes' &&
                              cta1ColumnWidth
                                ? { width: `${cta1ColumnWidth}px` }
                                : {}),
                            }
                          : fields.displayCTAButton === 'yes' && cta1ColumnWidth
                            ? { width: `${cta1ColumnWidth}px` }
                            : undefined
                      }
                    >
                      {fields.displayCTAButton === 'yes' && (
                        <CTAButtonWrapper ref={cta1ButtonRef}>
                          <KitButton
                            round
                            size="lg"
                            style={{
                              backgroundColor: buttonColor(
                                theme,
                                fields?.buttonColor,
                              ),
                              border:
                                '1px solid ' +
                                buttonColor(theme, fields?.buttonBorderColor),
                              color: buttonColor(
                                theme,
                                fields?.buttonTextColor,
                              ),
                              fontWeight: 600,
                            }}
                            onClick={() =>
                              handleCTAClick({
                                ctaLinkType: fields?.ctaLinkType,
                                ctaLink: fields?.ctaLink,
                                ctaURL: fields?.ctaURL,
                                ctaVideoEmbedCode: fields?.ctaVideoEmbedCode,
                              })
                            }
                          >
                            {fields.ctaButtonText}
                          </KitButton>
                        </CTAButtonWrapper>
                      )}
                      {fields.ctaDisclaimerText &&
                        fields.ctaDisclaimerContent && (
                          <DisclaimerContainer
                            className={cn(
                              fields.ctaDisclaimerTextFontColor?.fields?.value
                                ? fields.ctaDisclaimerTextFontColor.fields
                                    ?.value
                                : 'text-gray-600',
                              'text-xs',
                            )}
                          >
                            <AIDisclaimer
                              linkText={fields.ctaDisclaimerText}
                              disclaimer={fields.ctaDisclaimerContent || ''}
                            />
                          </DisclaimerContainer>
                        )}
                    </CTAColumn>
                  )}

                  {(fields.displayCTA2Button === 'yes' ||
                    (fields.cta2DisclaimerText &&
                      fields.cta2DisclaimerContent)) && (
                    <CTAColumn
                      style={
                        displayContentInCard
                          ? {
                              alignItems: 'center',
                              ...(fields.displayCTA2Button === 'yes' &&
                              cta2ColumnWidth
                                ? { width: `${cta2ColumnWidth}px` }
                                : {}),
                            }
                          : fields.displayCTA2Button === 'yes' &&
                              cta2ColumnWidth
                            ? { width: `${cta2ColumnWidth}px` }
                            : undefined
                      }
                    >
                      {fields.displayCTA2Button === 'yes' && (
                        <CTAButtonWrapper ref={cta2ButtonRef}>
                          <KitButton
                            round
                            size="lg"
                            style={{
                              backgroundColor: buttonColor(
                                theme,
                                fields?.button2Color,
                              ),
                              border:
                                '1px solid ' +
                                buttonColor(theme, fields?.button2BorderColor),
                              color: buttonColor(
                                theme,
                                fields?.button2TextColor,
                              ),
                              fontWeight: 600,
                            }}
                            onClick={() =>
                              handleCTAClick({
                                ctaLinkType: fields?.cta2LinkType,
                                ctaLink: fields?.cta2Link,
                                ctaURL: fields?.cta2URL,
                                ctaVideoEmbedCode: fields?.cta2VideoEmbedCode,
                              })
                            }
                          >
                            {fields.cta2ButtonText}
                          </KitButton>
                        </CTAButtonWrapper>
                      )}
                      {fields.cta2DisclaimerText &&
                        fields.cta2DisclaimerContent && (
                          <DisclaimerContainer
                            className={cn(
                              fields.cta2DisclaimerTextFontColor?.fields?.value
                                ? fields.cta2DisclaimerTextFontColor.fields
                                    ?.value
                                : 'text-gray-600',
                              'text-xs',
                            )}
                          >
                            <AIDisclaimer
                              linkText={fields.cta2DisclaimerText}
                              disclaimer={fields.cta2DisclaimerContent || ''}
                            />
                          </DisclaimerContainer>
                        )}
                    </CTAColumn>
                  )}
                </CTAColumnsContainer>
              </ButtonContainer>
            </ContentContainer>
          </TextContainer>
        </VideoContainer>
      </Container>
      <MobileContainer
        style={{
          backgroundImage:
            fields.noGradient === 'true'
              ? `url(${fields.backgroundImage.url})`
              : `linear-gradient(rgba(0, 0, 0, 0.7),rgba(0, 0, 0, 0.6)), url(${fields.backgroundImage.url})`,
          backgroundColor: theme.palette.primary.dark,
        }}
      >
        <TextContainer
          style={displayContentInCard ? { marginTop: 0 } : undefined}
        >
          <ContentContainer displayContentInCard={displayContentInCard}>
            <FreeformHeader
              level={'h2'}
              text={fields?.headingText}
              color={headingColor(theme, fields?.headingStyle?.fields?.color)}
              style={{
                marginTop: displayContentInCard ? 0 : '1.5rem',
                textAlign: displayContentInCard ? 'center' : undefined,
              }}
            />
            {hasSubtitle && (
              <CustomSubTitle
                subtitle={fields.subtitle}
                subTitleStyle={{
                  textAlign: subTitleStyle?.alignment || '',
                  color: subTitleStyle?.textColor || '#FFF',
                  fontFamily: 'Source Sans Pro',
                  fontStyle: 'normal',
                  marginTop: subTitleStyle?.marginTop || 0,
                  marginBottom: subTitleStyle?.marginBottom || 0,
                  ...(subTitleStyle || {}),
                }}
              />
            )}

            <MobileDescription
              hasSubtitle={hasSubtitle || false}
              style={
                displayContentInCard
                  ? {
                      textAlign: 'center',
                    }
                  : undefined
              }
            >
              {fields.descriptionText}
            </MobileDescription>
            {(fields.displayCTAButton === 'yes' ||
              fields.displayCTA2Button === 'yes' ||
              (fields.ctaDisclaimerText && fields.ctaDisclaimerContent) ||
              (fields.cta2DisclaimerText && fields.cta2DisclaimerContent)) && (
              <ButtonContainer
                style={
                  displayContentInCard
                    ? { alignSelf: 'center', marginBottom: 0 }
                    : undefined
                }
              >
                <CTAColumnsContainer
                  style={
                    displayContentInCard ? { alignItems: 'center' } : undefined
                  }
                >
                  {(fields.displayCTAButton === 'yes' ||
                    (fields.ctaDisclaimerText &&
                      fields.ctaDisclaimerContent)) && (
                    <CTAColumn
                      style={
                        displayContentInCard
                          ? { alignItems: 'center' }
                          : undefined
                      }
                    >
                      {fields.displayCTAButton === 'yes' && (
                        <KitButton
                          round
                          size="sm"
                          style={{
                            backgroundColor:
                              fields?.buttonColor === 'transparent'
                                ? 'transparent'
                                : buttonColor(theme, fields?.buttonColor),
                            border:
                              '1px solid ' +
                              buttonColor(theme, fields?.buttonBorderColor),
                            color: buttonColor(theme, fields?.buttonTextColor),
                          }}
                          onClick={() =>
                            handleCTAClick({
                              ctaLinkType: fields?.ctaLinkType,
                              ctaLink: fields?.ctaLink,
                              ctaURL: fields?.ctaURL,
                              ctaVideoEmbedCode: fields?.ctaVideoEmbedCode,
                            })
                          }
                        >
                          {fields.ctaButtonText}
                        </KitButton>
                      )}
                      {fields.ctaDisclaimerText &&
                        fields.ctaDisclaimerContent && (
                          <DisclaimerContainer
                            className={cn(
                              fields.ctaDisclaimerTextFontColor?.fields?.value
                                ? fields.ctaDisclaimerTextFontColor.fields
                                    ?.value
                                : 'text-gray-600',
                              'text-xs',
                            )}
                          >
                            <AIDisclaimer
                              linkText={fields.ctaDisclaimerText}
                              disclaimer={fields.ctaDisclaimerContent || ''}
                            />
                          </DisclaimerContainer>
                        )}
                    </CTAColumn>
                  )}

                  {(fields.displayCTA2Button === 'yes' ||
                    (fields.cta2DisclaimerText &&
                      fields.cta2DisclaimerContent)) && (
                    <CTAColumn
                      style={
                        displayContentInCard
                          ? { alignItems: 'center' }
                          : undefined
                      }
                    >
                      {fields.displayCTA2Button === 'yes' && (
                        <KitButton
                          round
                          size="sm"
                          style={{
                            backgroundColor:
                              fields?.button2Color === 'transparent'
                                ? 'transparent'
                                : buttonColor(theme, fields?.button2Color),
                            border:
                              '1px solid ' +
                              buttonColor(theme, fields?.button2BorderColor),
                            color: buttonColor(theme, fields?.button2TextColor),
                          }}
                          onClick={() =>
                            handleCTAClick({
                              ctaLinkType: fields?.cta2LinkType,
                              ctaLink: fields?.cta2Link,
                              ctaURL: fields?.cta2URL,
                              ctaVideoEmbedCode: fields?.cta2VideoEmbedCode,
                            })
                          }
                        >
                          {fields.cta2ButtonText}
                        </KitButton>
                      )}
                      {fields.cta2DisclaimerText &&
                        fields.cta2DisclaimerContent && (
                          <DisclaimerContainer
                            className={cn(
                              fields.cta2DisclaimerTextFontColor?.fields?.value
                                ? fields.cta2DisclaimerTextFontColor.fields
                                    ?.value
                                : 'text-gray-600',
                              'text-xs',
                            )}
                          >
                            <AIDisclaimer
                              linkText={fields.cta2DisclaimerText}
                              disclaimer={fields.cta2DisclaimerContent || ''}
                            />
                          </DisclaimerContainer>
                        )}
                    </CTAColumn>
                  )}
                </CTAColumnsContainer>
              </ButtonContainer>
            )}
          </ContentContainer>
        </TextContainer>
      </MobileContainer>
      <VideoDialogWithX
        size="md"
        content={
          <VideoContainer id="video-container">
            {shouldLoadVideoEmbed ? (
              <EmbedContainer
                id="embed-container"
                className="prose max-w-full mx-auto"
                dangerouslySetInnerHTML={renderHTML(selectedVideoEmbedCode)}
              />
            ) : (
              <VideoPlaceholderContainer>
                <KitButton
                  round
                  size="lg"
                  style={{
                    backgroundColor: theme.palette.common.white,
                    border: `1px solid ${theme.palette.common.white}`,
                    color: theme.palette.primary.dark,
                    fontWeight: 700,
                  }}
                  onClick={() => setShouldLoadVideoEmbed(true)}
                >
                  Play video
                </KitButton>
              </VideoPlaceholderContainer>
            )}
          </VideoContainer>
        }
        ariaLabel="View Video"
        showValue={showVideoDialog}
        showMethod={handleVideoDialogVisibilityChange}
      />
    </React.Fragment>
  );
};
export default TopBannerWithHeading;
